import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import { message } from '@ui/message';
import {
  DashboardOutlined,
  LaptopOutlined,
  LineChartOutlined,
  CodeOutlined,
  GlobalOutlined,
  MessageOutlined,
  MailOutlined,
  KeyOutlined,
} from '@ui/icons';
import '@lib/api';
import '@lib/brand';
import '@lib/utils';
import { API_BASE } from '@lib/api';
import { AuthModule, type CurrentUser } from '@lib/auth';
import { PidocBrand } from '@lib/brand';
import { t, init as i18nInit, getLanguage, setLanguage, getSupportedLanguages } from '@lib/i18n';
import '../styles/fonts';

// Feature 组件（ESM 导入）
import { DeviceManagement } from '@features/device/DeviceManagement';
import { FullMetrics } from '@features/metrics/FullMetrics';
import { Overview } from '@features/overview/Overview';
import { MessageCenter } from '@features/message/MessageCenter';
import { ForwardManagement } from '@features/forward/WebForwardManagement';
import { KeyManagement } from '@features/install-token/KeyManagement';

const { useState, useEffect, useRef } = React;

// 初始化多语言
i18nInit();

// ── Language Selector ────────────────────────────────────
interface LanguageSelectorProps {
  isMobile: boolean;
}

function LanguageSelector({ isMobile }: LanguageSelectorProps) {
  const [currentLang, setCurrentLang] = useState<string>('zh-CN');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setCurrentLang(getLanguage());
  }, []);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleChange = (lang: string) => {
    setLanguage(lang);
    window.location.reload();
  };
  const languages = getSupportedLanguages();

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-dark-300 hover:text-white transition-colors text-sm"
      >
        <GlobalOutlined className="w-4 h-4" />
        {!isMobile && <span>{languages[currentLang as keyof typeof languages]}</span>}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 min-w-[140px] bg-dark-800 border border-dark-700 rounded-lg shadow-xl z-50 py-1">
          {Object.entries(languages).map(([code, name]) => (
            <div
              key={code}
              onClick={() => {
                handleChange(code);
                setOpen(false);
              }}
              className={`px-4 py-2 text-sm cursor-pointer ${code === currentLang ? 'bg-primary-500/20 text-primary-300' : 'text-dark-200 hover:bg-dark-700'}`}
            >
              {name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main App ─────────────────────────────────────────────
type View = 'login' | 'register' | 'forgot-password' | 'reset-password';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [authChecking, setAuthChecking] = useState(true);
  const [currentView, setCurrentView] = useState<View>('login');
  const [resetToken, setResetToken] = useState('');

  useEffect(() => {
    // 优先处理密码重置链接（无需登录态）
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    if (tokenParam) {
      setResetToken(tokenParam);
      setCurrentView('reset-password');
      setAuthChecking(false);
      return;
    }
    // 通过 Cookie 向服务端拉取当前用户（HttpOnly Cookie 自动携带）
    AuthModule.loadUser()
      .then((u) => {
        if (u) {
          setUser(u);
          setIsLoggedIn(true);
        }
      })
      .finally(() => setAuthChecking(false));
  }, []);

  useEffect(() => {
    if (isLoggedIn) document.body.classList.remove('login-page');
    else document.body.classList.add('login-page');
  }, [isLoggedIn]);

  const handleLoginSuccess = (u: CurrentUser | null) => {
    setUser(u);
    setIsLoggedIn(true);
  };

  const handleLogout = async () => {
    await AuthModule.logout();
    // logout 内部会跳转 '/'；以下是兜底
    setIsLoggedIn(false);
    setUser(null);
  };

  if (authChecking) {
    return (
      <div className="flex items-center justify-center h-screen w-full bg-dark-950">
        <div className="text-dark-400">{t('common.loading')}</div>
      </div>
    );
  }

  if (!isLoggedIn) {
    if (currentView === 'register')
      return <RegisterPage onSwitchToLogin={() => setCurrentView('login')} />;
    if (currentView === 'forgot-password')
      return <ForgotPasswordPage onSwitchToLogin={() => setCurrentView('login')} />;
    if (currentView === 'reset-password')
      return (
        <ResetPasswordPage
          token={resetToken}
          onComplete={() => {
            setCurrentView('login');
            window.history.replaceState({}, '', '/');
          }}
        />
      );
    return (
      <LoginPage
        onLogin={handleLoginSuccess}
        onSwitchToRegister={() => setCurrentView('register')}
        onSwitchToForgotPassword={() => setCurrentView('forgot-password')}
      />
    );
  }
  return <Dashboard user={user} onLogout={handleLogout} />;
}

// ── Login Page ──────────────────────────────────────────
interface LoginPageProps {
  onLogin: (user: CurrentUser | null) => void;
  onSwitchToRegister: () => void;
  onSwitchToForgotPassword: () => void;
}

function LoginPage({ onLogin, onSwitchToRegister, onSwitchToForgotPassword }: LoginPageProps) {
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const values = {
      username: formData.get('username') as string,
      password: formData.get('password') as string,
    };
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin', // 接收 Set-Cookie
        body: JSON.stringify(values),
      });
      const data = (await response.json()) as {
        code?: number;
        message?: string;
        data?: { user?: CurrentUser | null };
      };
      if (response.ok && data.code === 200) {
        // Cookie 已由服务端设置，前端只需更新内存中的用户信息
        const u = data.data?.user || null;
        AuthModule.setUser(u);
        message.success(t('login.loginSuccess'));
        onLogin(u);
      } else if (data.code === 429) {
        // 触发失败限流，后端 message 是英文，这里统一用本地化文案
        message.error(t('login.tooManyAttempts'));
      } else {
        message.error(data.message || t('login.loginFailed'));
      }
    } catch {
      message.error(t('common.networkError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container max-w-md mx-auto mt-12 p-8 bg-dark-900/50 rounded-2xl border border-white/5 shadow-xl backdrop-blur-sm">
      <div className="flex justify-between items-center mb-8">
        <div className="text-2xl font-bold text-white">{t('login.title')}</div>
        <LanguageSelector isMobile={false} />
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-dark-300 mb-2">
            {t('login.usernamePlaceholder')}
          </label>
          <input
            name="username"
            type="text"
            required
            autoComplete="username"
            disabled={loading}
            className="w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder={t('login.usernamePlaceholder')}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-dark-300 mb-2">
            {t('login.passwordPlaceholder')}
          </label>
          <input
            name="password"
            type="password"
            required
            autoComplete="current-password"
            disabled={loading}
            className="w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder={t('login.passwordPlaceholder')}
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-500 text-white font-semibold rounded-lg transition-all shadow-lg shadow-primary-600/25 hover:shadow-primary-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? t('common.loading') : t('login.loginButton')}
        </button>
        <div className="text-center space-y-3 pt-4">
          <button
            type="button"
            onClick={onSwitchToRegister}
            className="text-primary-400 hover:text-primary-300 text-sm font-medium"
          >
            {t('login.noAccount')}
          </button>
          <br />
          <button
            type="button"
            onClick={onSwitchToForgotPassword}
            className="text-primary-400 hover:text-primary-300 text-sm font-medium"
          >
            {t('login.forgotPassword')}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Register Page ────────────────────────────────────────
interface RegisterPageProps {
  onSwitchToLogin: () => void;
}

function RegisterPage({ onSwitchToLogin }: RegisterPageProps) {
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const values = {
      username: String(fd.get('username') ?? ''),
      email: String(fd.get('email') ?? ''),
      password: String(fd.get('password') ?? ''),
      confirm: String(fd.get('confirm') ?? ''),
    };

    // Validate
    const errs: Record<string, string> = {};
    if (!values.username || values.username.length < 3)
      errs.username = t('register.usernameRequired');
    if (!values.email || !values.email.includes('@')) errs.email = t('register.emailRequired');
    if (!values.password || values.password.length < 6)
      errs.password = t('register.passwordRequired');
    if (values.password !== values.confirm) errs.confirm = t('register.confirmPasswordMismatch');
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': getLanguage() || 'zh-CN',
        },
        body: JSON.stringify(values),
      });
      const data = (await response.json()) as {
        code?: number;
        message?: string;
        data?: { email_verified?: boolean };
      };
      if (response.ok && data.code === 200) {
        message.success(data.message || t('register.registerSuccess'));
        if (data.data?.email_verified === false) {
          setTimeout(() => message.info(t('register.emailSent')), 500);
        }
        let seconds = 5;
        setCountdown(seconds);
        const timer = setInterval(() => {
          seconds--;
          setCountdown(seconds);
          if (seconds <= 0) {
            clearInterval(timer);
            onSwitchToLogin();
          }
        }, 1000);
      } else {
        message.error(data.message || t('register.registerFailed'));
      }
    } catch {
      message.error(t('common.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    'w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent';
  const errCls = (f: string) => (errors[f] ? 'border-red-500 focus:ring-red-500' : '');

  return (
    <div className="login-container max-w-md mx-auto mt-12 p-8 bg-dark-900/50 rounded-2xl border border-white/5 shadow-xl backdrop-blur-sm">
      <div className="flex justify-between items-center mb-8">
        <div className="text-2xl font-bold text-white">{t('register.title')}</div>
        <LanguageSelector isMobile={false} />
      </div>
      {countdown !== null && (
        <div className="mb-6 p-4 bg-primary-500/10 border border-primary-500/20 rounded-lg text-center">
          <div className="text-primary-300 mb-1">
            ✅ {t('register.registerSuccess')} {t('register.emailSent')}
          </div>
          <div className="text-xs text-dark-400">
            {t('register.redirectingIn', { seconds: countdown })}
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <input
            name="username"
            type="text"
            required
            autoComplete="username"
            minLength={3}
            maxLength={20}
            placeholder={t('register.usernamePlaceholder')}
            disabled={loading}
            className={`${inputCls} ${errCls('username')}`}
          />
          {errors.username && <div className="text-red-400 text-xs mt-1">{errors.username}</div>}
        </div>
        <div>
          <input
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder={t('register.emailPlaceholder')}
            disabled={loading}
            className={`${inputCls} ${errCls('email')}`}
          />
          {errors.email && <div className="text-red-400 text-xs mt-1">{errors.email}</div>}
        </div>
        <div>
          <input
            name="password"
            type="password"
            required
            autoComplete="new-password"
            minLength={6}
            placeholder={t('register.passwordPlaceholder')}
            disabled={loading}
            className={`${inputCls} ${errCls('password')}`}
          />
          {errors.password && <div className="text-red-400 text-xs mt-1">{errors.password}</div>}
        </div>
        <div>
          <input
            name="confirm"
            type="password"
            required
            autoComplete="new-password"
            placeholder={t('register.confirmPasswordPlaceholder')}
            disabled={loading}
            className={`${inputCls} ${errCls('confirm')}`}
          />
          {errors.confirm && <div className="text-red-400 text-xs mt-1">{errors.confirm}</div>}
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-500 text-white font-semibold rounded-lg transition-all shadow-lg shadow-primary-600/25 disabled:opacity-50"
        >
          {t('register.registerButton')}
        </button>
        <div className="text-center">
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="text-primary-400 hover:text-primary-300 text-sm font-medium"
          >
            {t('register.haveAccount')}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Forgot Password Page ─────────────────────────────────
interface ForgotPasswordPageProps {
  onSwitchToLogin: () => void;
}

function ForgotPasswordPage({ onSwitchToLogin }: ForgotPasswordPageProps) {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': getLanguage() || 'zh-CN',
        },
        body: JSON.stringify({ email: fd.get('email') }),
      });
      const data = (await response.json()) as { code?: number; message?: string };
      if (response.ok && data.code === 200) {
        setSubmitted(true);
        if (data.message) message.success(data.message);
      } else message.error(data.message || t('common.error'));
    } catch {
      message.error(t('common.networkError'));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="login-container max-w-md mx-auto mt-12 p-8 bg-dark-900/50 rounded-2xl border border-white/5 shadow-xl backdrop-blur-sm">
        <div className="flex justify-between items-center mb-8">
          <div className="text-2xl font-bold text-white">{t('forgotPassword.emailSent')}</div>
          <LanguageSelector isMobile={false} />
        </div>
        <div className="text-center py-8">
          <div className="text-5xl mb-4">
            <MailOutlined />
          </div>
          <div className="text-white font-semibold mb-2">{t('forgotPassword.successMessage')}</div>
          <div
            className="text-dark-400 text-sm mb-6"
            dangerouslySetInnerHTML={{ __html: t('forgotPassword.instruction') }}
          />
          <button
            onClick={onSwitchToLogin}
            className="px-6 py-2.5 bg-primary-600 hover:bg-primary-500 text-white font-medium rounded-lg transition-all"
          >
            {t('forgotPassword.backButton')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container max-w-md mx-auto mt-12 p-8 bg-dark-900/50 rounded-2xl border border-white/5 shadow-xl backdrop-blur-sm">
      <div className="flex justify-between items-center mb-8">
        <div className="text-2xl font-bold text-white">{t('forgotPassword.title')}</div>
        <LanguageSelector isMobile={false} />
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <input
            name="email"
            type="email"
            required
            placeholder={t('forgotPassword.emailPlaceholder')}
            disabled={loading}
            className="w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-500 text-white font-semibold rounded-lg transition-all disabled:opacity-50"
        >
          {t('forgotPassword.sendButton')}
        </button>
        <div className="text-center">
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="text-primary-400 hover:text-primary-300 text-sm font-medium"
          >
            {t('forgotPassword.backButton')}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Reset Password Page ──────────────────────────────────
interface ResetPasswordPageProps {
  token: string;
  onComplete: () => void;
}

function ResetPasswordPage({ token, onComplete }: ResetPasswordPageProps) {
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const newPwd = String(fd.get('new_password') ?? '');
    const confirmPwd = String(fd.get('confirm_password') ?? '');
    if (newPwd !== confirmPwd) {
      message.error(t('resetPassword.confirmPasswordMismatch'));
      return;
    }
    if (newPwd.length < 6) {
      message.error(t('resetPassword.newPasswordMin'));
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPwd }),
      });
      const data = (await response.json()) as { code?: number; message?: string };
      if (response.ok && data.code === 200) {
        if (data.message) message.success(data.message);
        setTimeout(onComplete, 1500);
      } else message.error(data.message || t('common.error'));
    } catch {
      message.error(t('common.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    'w-full px-4 py-3 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent';

  return (
    <div className="login-container max-w-md mx-auto mt-12 p-8 bg-dark-900/50 rounded-2xl border border-white/5 shadow-xl backdrop-blur-sm">
      <div className="flex justify-between items-center mb-8">
        <div className="text-2xl font-bold text-white">{t('resetPassword.title')}</div>
        <LanguageSelector isMobile={false} />
      </div>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <input
            name="new_password"
            type="password"
            required
            autoComplete="new-password"
            minLength={6}
            placeholder={t('resetPassword.newPasswordPlaceholder')}
            disabled={loading}
            className={inputCls}
          />
        </div>
        <div>
          <input
            name="confirm_password"
            type="password"
            required
            autoComplete="new-password"
            placeholder={t('resetPassword.confirmPasswordPlaceholder')}
            disabled={loading}
            className={inputCls}
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-500 text-white font-semibold rounded-lg transition-all disabled:opacity-50"
        >
          {t('resetPassword.resetButton')}
        </button>
      </form>
    </div>
  );
}

// ── Dashboard ────────────────────────────────────────────
type MenuKey = 'overview' | 'devices' | 'metrics' | 'bandwidth' | 'messages' | 'ssh' | 'forward' | 'keys';
const MENU_KEYS_WITHOUT_SSH: readonly MenuKey[] = [
  'overview',
  'devices',
  'metrics',
  'messages',
  'forward',
  'keys',
];

interface DashboardProps {
  user: CurrentUser | null;
  onLogout: () => void;
}

interface DashboardMenuItem {
  key: MenuKey;
  icon: React.FC<{ className?: string }>;
  label: string;
  href?: string;
}

function Dashboard({ user: userFromProps, onLogout }: DashboardProps) {
  const [user, setUser] = useState<CurrentUser | null>(userFromProps || null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const getMenuFromHash = (): MenuKey => {
    const hash = window.location.hash.slice(1);
    if ((MENU_KEYS_WITHOUT_SSH as readonly string[]).includes(hash)) return hash as MenuKey;
    return 'overview';
  };
  const [currentMenu, setCurrentMenu] = useState<MenuKey>(getMenuFromHash());

  useEffect(() => {
    // 优先使用 props 传入的 user；否则从 AuthModule 读取内存缓存
    if (!user) {
      const cached = AuthModule.getCurrentUser();
      if (cached) setUser(cached);
    }
    const handleHashChange = () => setCurrentMenu(getMenuFromHash());
    let resizeTimer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => setIsMobile(window.innerWidth <= 768), 100);
    };
    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('resize', handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMenuChange = (key: MenuKey) => {
    if (key === 'ssh') return;
    window.location.hash = key;
    setCurrentMenu(key);
  };

  const renderContent = () => {
    switch (currentMenu) {
      case 'metrics':
        return <FullMetrics />;
      case 'devices':
        return <DeviceManagement />;
      case 'messages':
        return <MessageCenter />;
      case 'forward':
        return <ForwardManagement />;
      case 'keys':
        return <KeyManagement />;
      default:
        return <Overview user={user} />;
    }
  };

  const menuItems: DashboardMenuItem[] = [
    { key: 'overview', icon: DashboardOutlined, label: t('dashboard.overview') },
    { key: 'metrics', icon: LineChartOutlined, label: t('dashboard.metrics') },
    { key: 'devices', icon: LaptopOutlined, label: t('dashboard.devices') },
    { key: 'keys', icon: KeyOutlined, label: t('dashboard.keys') },
    { key: 'forward', icon: GlobalOutlined, label: t('dashboard.forward') },
    { key: 'ssh', icon: CodeOutlined, label: t('dashboard.ssh'), href: '/static/ssh.html' },
  ];

  const SidebarIcon = ({ icon: Icon }: { icon: DashboardMenuItem['icon'] }) => (
    <Icon className="w-5 h-5" />
  );

  return (
    <div className="min-h-screen bg-dark-950 flex flex-col">
      {/* Header */}
      <header className="h-14 bg-dark-900/80 backdrop-blur-md border-b border-dark-800 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-primary-500/20">
            P
          </div>
          <span className="text-lg font-bold text-white tracking-tight">PiDOC</span>
        </div>
        <div className="flex items-center gap-4">
          {!isMobile && user && (
            <span className="text-sm text-dark-400">
              {t('common.welcome', { username: user.username })}
            </span>
          )}
          <LanguageSelector isMobile={isMobile} />
          <button
            onClick={() => handleMenuChange('messages')}
            title={t('dashboard.messages')}
            className={`flex items-center gap-1.5 text-sm transition-colors ${currentMenu === 'messages' ? 'text-primary-400' : 'text-dark-400 hover:text-white'}`}
          >
            <MessageOutlined className="w-4 h-4" />
            {!isMobile && <span>{t('dashboard.messages')}</span>}
          </button>
          <a
            href={PidocBrand.edition === 'global' ? `${PidocBrand.docsURL}/guide.html` : `${PidocBrand.docsURL}/docs/pidoc/install_monitor`}
            target="_blank"
            rel="noreferrer"
            className="text-sm text-dark-400 hover:text-white transition-colors hidden md:inline"
          >
            {t('common.help')}
          </a>
          <button
            onClick={onLogout}
            className="text-sm text-dark-400 hover:text-white transition-colors"
          >
            {t('common.logout')}
          </button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Desktop Sidebar */}
        {!isMobile && (
          <nav className="w-48 bg-dark-900/50 border-r border-dark-800 flex-shrink-0">
            <div className="py-4">
              {menuItems.map((item) =>
                item.href ? (
                  <a
                    key={item.key}
                    href={item.href}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 px-5 py-2.5 text-sm text-dark-400 hover:text-white hover:bg-dark-800/50 transition-colors"
                  >
                    <SidebarIcon icon={item.icon} />
                    <span>{item.label}</span>
                  </a>
                ) : (
                  <button
                    key={item.key}
                    onClick={() => handleMenuChange(item.key)}
                    className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-colors text-left ${currentMenu === item.key ? 'text-primary-400 bg-primary-500/10 border-r-2 border-primary-500' : 'text-dark-400 hover:text-white hover:bg-dark-800/50'}`}
                  >
                    <SidebarIcon icon={item.icon} />
                    <span>{item.label}</span>
                  </button>
                ),
              )}
            </div>
          </nav>
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-3 md:p-6">
          <div className="animate-fade-in">{renderContent()}</div>
          <footer className="mt-12 py-6 border-t border-dark-800 text-center text-dark-500 text-xs">
            <div>{t('common.copyright')}</div>
            <div className="mt-2">{t('common.feedback')}</div>
          </footer>
          {/* 底部导航占位，仅移动端生效（高度需覆盖导航栏 + 安全区） */}
          {isMobile && <div className="h-16" style={{ height: 'calc(56px + env(safe-area-inset-bottom, 0px))' }} />}
        </main>
      </div>

      {/* Mobile Bottom Nav */}
      {isMobile && (
        <div className="mobile-bottom-nav">
          {menuItems
            .filter((i) => i.key !== 'ssh')
            .map((item) => (
              <button
                key={item.key}
                className={`mobile-nav-item ${currentMenu === item.key ? 'active' : ''}`}
                onClick={() => handleMenuChange(item.key)}
              >
                <span className="nav-icon">
                  <SidebarIcon icon={item.icon} />
                </span>
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

// ── Render ───────────────────────────────────────────────
const rootEl = document.getElementById('root');
if (rootEl) {
  ReactDOM.createRoot(rootEl).render(<App />);
}
